import { getPoolWebClientId } from '@/shared/aws';
import { Auth } from 'aws-amplify';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import {
  AUTH_MODE,
  CLIENT_NAMES,
  EMITTER_CONSTANTS,
  ERROR_ACTION,
  URL_CALLCENTER_PORTAL,
} from '@/shared/config';
import { switchAmplifyAuthMode } from '@/shared/lib';
import { useEmitter, useMergeState, useWindowEvent } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { initializeGtm, isAllowedGtm } from '@/shared/lib/gtm';
import socketio from '@/shared/lib/socketio';
import { useLoadingStore } from '@/shared/lib/loadingStore';
import { useUserStore } from '@/entities/user';
import {
  clearCookies,
  communicateToErpPortal,
  emitLogoutEvent,
  handleCheckRoles,
  messageHandler,
} from './authenticationGate.h';

/**
 * Drives the authentication gate: probes the login mode, wires cross-tab logout and session expiry, and boots the socket plus the current-user fetch once authenticated. Runs checkLoginStatus on mount, listens on storage for the logout and session-expired keys, attaches the SSO message handler while unauthenticated, validates the fetched user's roles and GTM eligibility, and reacts to expired-token, logout and maintenance emitter events.
 *
 * @returns An object carrying the gate state plus the onLoad and signOutEvent callbacks.
 */
const useAuthenticationGate = () => {
  const meRedux = useUserStore((s) => s.me);
  const isStorageSigningOut = useRef(false);

  const fetchMeRequestAction = useUserStore((s) => s.fetchMe);
  const loadingPageAction = useLoadingStore((s) => s.loadingPage);

  const [state, setState] = useMergeState({
    isCheckedAuthMode: false,
    isLoggedInWithUsername: false,
    isLoadedData: false,
    isShowModalExpriedToken: false,
  });

  /**
   * Requests the AI report bootstrap payload from the host ERP portal once the gate is ready. Posts a get message keyed getDataAiReport through communicateToErpPortal.
   *
   * @returns Nothing.
   */
  const onLoad = () => {
    communicateToErpPortal({
      action: 'get',
      key: 'getDataAiReport',
    });
  };

  /**
   * Signs the user out of the AI report app and sends the browser to the call-center portal. Falls back to the portal URL when redirectUrl is not a string, broadcasts the logout event to other tabs, clears cookies and localStorage, then assigns document.location.href.
   *
   * @param redirectUrl - Absolute URL to land on after sign-out; anything non-string falls back to the call-center portal.
   * @returns Nothing.
   */
  const signOutEvent = (redirectUrl = URL_CALLCENTER_PORTAL) => {
    const nextRedirectUrl = typeof redirectUrl === 'string' ? redirectUrl : URL_CALLCENTER_PORTAL;

    emitLogoutEvent(nextRedirectUrl);
    clearCookies();
    localStorage.clear();
    document.location.href = nextRedirectUrl;
  };

  /**
   * Completes a sign-out that was initiated in another browser tab. Guards re-entry with a ref, disconnects the socket, clears cookies, removes the logout-event key from localStorage and redirects the document.
   *
   * @param redirectUrl - Absolute URL to land on after the cross-tab sign-out.
   * @returns Nothing.
   */
  const handleSignOutByStorage = (redirectUrl = URL_CALLCENTER_PORTAL) => {
    if (isStorageSigningOut.current) {
      return;
    }

    isStorageSigningOut.current = true;
    // socketio.disconnectSocket() takes no params (Task 4); this extra arg is a
    // pre-existing no-op since JS ignores it, kept as-is via a loose cast.
    (socketio.disconnectSocket as any)(false);
    clearCookies();
    localStorage.removeItem('logout-event');
    document.location.href = redirectUrl;
  };

  /**
   * Decides whether the current session is a username and password Cognito login or must fall back to SSO. Reads the LastAuthUser cookie for the configured user pool, and on a hit switches Amplify to username mode and confirms the session with Auth.currentAuthenticatedUser, signing out on failure; otherwise clears cookies and switches to SSO, recording the outcome in state.
   *
   * @returns A promise resolving once the auth mode has been probed and written to state.
   */
  const checkLoginStatus = async () => {
    const cookies = Object.fromEntries(document.cookie.split('; ').map((c) => c.split('=')));
    const userPoolWebClientId = getPoolWebClientId();
    const lastAuthUser =
      cookies[`CognitoIdentityServiceProvider.${userPoolWebClientId}.LastAuthUser`];
    if (lastAuthUser) {
      try {
        switchAmplifyAuthMode(AUTH_MODE.USERNAME_PASSWORD);
        await Auth.currentAuthenticatedUser();
        setState({
          isCheckedAuthMode: true,
          isLoggedInWithUsername: true,
        });
      } catch (error) {
        console.error('Failed to get current authenticated user: ', error);
        signOutEvent();
      }
    } else {
      console.log('No active username/password session found, fallback to SSO if possible');
      clearCookies();
      switchAmplifyAuthMode(AUTH_MODE.SSO);
      setState({
        isCheckedAuthMode: true,
        isLoggedInWithUsername: false,
      });
    }
  };

  useEffect(() => {
    checkLoginStatus();
  }, []);

  // Listen for logout/session expired from other tabs
  useWindowEvent('storage', ((event: StorageEvent) => {
    if (event.storageArea !== localStorage || !event.newValue) return;

    switch (event.key) {
      case 'logout-event':
        try {
          const { redirectUrl } = JSON.parse(event.newValue);
          handleSignOutByStorage(redirectUrl);
        } catch (error) {
          console.error('Failed to parse logout event: ', error);
        }
        break;
      case 'session-expired-event':
        loadingPageAction();
        setState({ isShowModalExpriedToken: true });
        break;
      default:
        break;
    }
  }) as EventListenerOrEventListenerObject);

  useEffect(() => {
    if (!state.isCheckedAuthMode || state.isLoggedInWithUsername) {
      return undefined;
    }

    window.addEventListener('message', messageHandler, false);
    return () => window.removeEventListener('message', messageHandler, false);
  }, [state.isCheckedAuthMode, state.isLoggedInWithUsername]);

  useEffect(() => {
    if (!state.isCheckedAuthMode || !state.isLoggedInWithUsername) {
      return;
    }

    socketio.connectToServer();
    fetchMeRequestAction();
  }, [fetchMeRequestAction, state.isCheckedAuthMode, state.isLoggedInWithUsername]);

  useEffect(() => {
    if (!_.isEmpty(meRedux)) {
      if (meRedux?.isDisabled) {
        emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
      } else {
        const isValid = handleCheckRoles(meRedux?.roles);
        if (isValid) {
          setState({ isLoadedData: true });
        } else {
          emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
        }
      }
    }
  }, [meRedux]);

  useEffect(() => {
    if (isAllowedGtm() && !_.isEmpty(meRedux)) {
      initializeGtm();
    }
  }, [meRedux]);

  useEmitter(
    EMITTER_CONSTANTS.EXPIRED_TOKEN,
    () => {
      localStorage.setItem(
        'session-expired-event',
        JSON.stringify({
          timestamp: Date.now(),
        }),
      );
      loadingPageAction();
      setState({ isShowModalExpriedToken: true });
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.UPDATE_TOKEN_LOCAL_STORAGE,
    async () => {
      await socketio.connectToServer();
      fetchMeRequestAction();
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.LOGOUT_REQUEST,
    () => {
      signOutEvent();
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.SITE_UNDER_MAINTENANCE,
    () => {
      loadingPageAction();
      window.location.reload();
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.MAINTENANCE_CHANGED,
    (msg: any) => {
      if (msg.isEnabled && _.intersection(CLIENT_NAMES, msg.clientNames).length > 0) {
        loadingPageAction();
        window.location.reload();
      }
    },
    [],
  );

  return { state, onLoad, signOutEvent };
};

export default useAuthenticationGate;
